import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./DatabasePage.css";
import { API_BASE } from "../api/config";

// Alle verfügbaren Spalten für die Query (Reihenfolge = SQL-Reihenfolge)
const ALL_QUERY_COLUMNS = [
  { key: "Auftragskonto", label: "Auftragskonto", default: true },
  { key: "Buchungstag", label: "Buchungstag", default: true },
  { key: "Valutadatum", label: "Valutadatum", default: false },
  { key: "Buchungstext", label: "Buchungstext", default: false },
  { key: "Verwendungszweck", label: "Verwendungszweck", default: true },
  { key: "Gläubiger ID", label: "Gläubiger ID", default: false },
  { key: "Mandatsreferenz", label: "Mandatsreferenz", default: false },
  { key: "Begünstigter", label: "Begünstigter", default: true },
  { key: "Kontonummer/IBAN", label: "Kontonummer/IBAN", default: true },
  { key: "BIC", label: "BIC", default: false },
  { key: "Betrag", label: "Betrag", default: true },
  { key: "Saldo", label: "Saldo", default: true },
  { key: "TT", label: "TT", default: false },
  { key: "MM", label: "MM", default: false },
  { key: "JJ", label: "JJ", default: false },
  { key: "KurzName", label: "KurzName", default: true },
  { key: "Kategorie", label: "Kategorie", default: true },
  { key: "SubKat", label: "SubKat", default: true },
  { key: "Kst", label: "Kst", default: true },
  { key: "MwSt", label: "MwSt", default: true },
  { key: "Umlageart", label: "Umlageart", default: true },
  { key: "BelegID", label: "BelegID", default: true },
  { key: "Gegenkonto", label: "Gegenkonto", default: true },
  { key: "Objekt", label: "Objekt", default: true },
  { key: "Kontocode", label: "Kontocode", default: false },
  { key: "ID", label: "ID", default: true },
  { key: "Status", label: "Status", default: false },
];

const DatabasePage = () => {
  const [konten, setKonten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKonto, setSelectedKonto] = useState(null);
  const [showMigrationPopup, setShowMigrationPopup] = useState(false);
  // Ansicht: "objekt" (Standard) oder "datei"
  const [ansicht, setAnsicht] = useState("objekt");
  const [ohneZuordnungOpen, setOhneZuordnungOpen] = useState(false);
  const navigate = useNavigate();

  // Query-Popup State
  const [queryColumns, setQueryColumns] = useState(
    ALL_QUERY_COLUMNS.filter(c => c.default).map(c => c.key)
  );
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [deutscheAusgabe, setDeutscheAusgabe] = useState(true);

  // Migration State
  const [migrationStep, setMigrationStep] = useState("upload");
  const [uploadId, setUploadId] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [dbColumns, setDbColumns] = useState([]);
  const [autoMapping, setAutoMapping] = useState({});
  const [mapping, setMapping] = useState({});
  const [auftragskonto, setAuftragskonto] = useState("");
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationError, setMigrationError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchObjektKonten();
  }, []);

  // Objekt-Ansicht: Konten mit Kontocode aus excel_mapping_config
  const fetchObjektKonten = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/database/konten_mapping`);
      if (!response.ok) throw new Error("Fehler beim Abrufen der Konten");
      const data = await response.json();
      setKonten(data.konten || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Datei-Ansicht: einfache Konten-Liste
  const fetchDateiKonten = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/database/konten`);
      if (!response.ok) throw new Error("Fehler beim Abrufen der Kontodaten");
      const data = await response.json();
      const transformedData = data.konten.map((konto) => ({
        ...konto,
        auftragskonto: konto.auftragskonto.replace(/^de/, "DE"),
      }));
      setKonten(transformedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPopup = (konto) => {
    setSelectedKonto(konto);
    setColumnsOpen(false);
  };

  const handleClosePopup = () => {
    setSelectedKonto(null);
  };

  const handleCopyToClipboard = () => {
    const code = buildQueryCode();
    navigator.clipboard.writeText(code)
      .then(() => alert("✅ Code erfolgreich kopiert!"))
      .catch(() => alert("❌ Fehler beim Kopieren des Codes."));
  };

  const buildQueryCode = () => {
    // Spalten-Liste für Table.SelectColumns
    const colList = queryColumns.map(c => `"${c}"`).join(", ");

    // Deutsche Formatierung: TransformColumns für Datum und Betrag
    let transformSteps = "";
    const dateTransforms = [];
    const numberTransforms = [];

    if (deutscheAusgabe) {
      if (queryColumns.includes("Buchungstag"))
        dateTransforms.push('{"Buchungstag", each try Date.ToText(Date.FromText(_), "dd.MM.yy") otherwise _}');
      if (queryColumns.includes("Valutadatum"))
        dateTransforms.push('{"Valutadatum", each try Date.ToText(Date.FromText(_), "dd.MM.yy") otherwise _}');
      if (queryColumns.includes("Betrag"))
        numberTransforms.push('{"Betrag", each try Number.ToText(Number.FromText(Text.Replace(Text.Replace(_, ".", ""), ",", ".")), "N2", "de-DE") & " €" otherwise _}');
      if (queryColumns.includes("Saldo"))
        numberTransforms.push('{"Saldo", each try Number.ToText(Number.FromText(Text.Replace(Text.Replace(_, ".", ""), ",", ".")), "N2", "de-DE") & " €" otherwise _}');
    }

    const allTransforms = [...dateTransforms, ...numberTransforms];
    if (allTransforms.length > 0) {
      transformSteps = `,\n    Formatiert = Table.TransformColumns(Gefiltert, {\n        ${allTransforms.join(",\n        ")}\n    })`;
    }

    const resultVar = allTransforms.length > 0 ? "Formatiert" : "Gefiltert";

    // Die Adresse kommt aus der Umgebung, nicht fest verdrahtet: Auf der
    // Produktivseite zog die Vorlage sonst Daten vom Testserver.
    const basis = API_BASE || "https://imho.sijelly.com";

    return `let
    APIKey = "b4dff0c5e72a4b6f9c342f8e90b1c2d8",
    Konto = "${selectedKonto || ""}",
    URL = if Konto = "" then
            "${basis}/database/get_daten"
          else
            "${basis}/database/get_daten?auftragskonto=" & Konto,

    Quelle = Json.Document(Web.Contents(URL, [Headers=[#"X-API-Key"=APIKey]])),
    Tabelle = Table.FromRecords(Quelle),
    Gefiltert = Table.SelectColumns(Tabelle, {${colList}})${transformSteps}
in
    ${resultVar}`;
  };

  const toggleColumn = (key) => {
    setQueryColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Ansicht wechseln
  const switchAnsicht = (mode) => {
    setAnsicht(mode);
    if (mode === "objekt") fetchObjektKonten();
    else fetchDateiKonten();
  };

  // Gruppierung der Konten für Objekt-Ansicht
  const mitZuordnung = konten.filter(k => k.kontocode);
  const ohneZuordnung = konten.filter(k => !k.kontocode);

  // ── Migration Handlers (unverändert) ──

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      setMigrationError("Nur .xlsx oder .xls Dateien erlaubt");
      return;
    }
    setUploading(true);
    setMigrationError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/database/migration/upload`, { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUploadId(data.upload_id);
      setUploadInfo(data);
      setDbColumns(data.db_columns || []);
      setAutoMapping(data.auto_mapping || {});
      setMapping(data.auto_mapping || {});
      setMigrationStep("mapping");
    } catch (e) {
      setMigrationError(e.message);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleSheetChange = async (sheet) => {
    if (!uploadId) return;
    setUploading(true);
    try {
      const res = await fetch(`${API_BASE}/database/migration/switch-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ upload_id: uploadId, sheet }),
      });
      const data = await res.json();
      if (data.success) {
        setUploadInfo(prev => ({
          ...prev,
          active_sheet: sheet,
          excel_headers: data.excel_headers,
          row_count: data.row_count,
          column_count: data.column_count,
          preview_rows: data.preview_rows,
        }));
        setAutoMapping(data.auto_mapping || {});
        setMapping(data.auto_mapping || {});
      }
    } catch (e) {
      setMigrationError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleMappingChange = (dbCol, excelCol) => {
    setMapping(prev => ({ ...prev, [dbCol]: excelCol }));
  };

  const startMigration = async () => {
    if (!uploadId) return;
    setMigrating(true);
    setMigrationError(null);
    setMigrationResult(null);
    try {
      const res = await fetch(`${API_BASE}/database/migration/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          upload_id: uploadId,
          mapping,
          auftragskonto: auftragskonto || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Migration fehlgeschlagen");
      setMigrationResult(data);
      setMigrationStep("done");
    } catch (e) {
      setMigrationError(e.message);
    } finally {
      setMigrating(false);
    }
  };

  const closeMigrationPopup = () => {
    setShowMigrationPopup(false);
    setMigrationStep("upload");
    setUploadId(null);
    setUploadInfo(null);
    setMapping({});
    setAutoMapping({});
    setAuftragskonto("");
    setMigrationResult(null);
    setMigrationError(null);
    setDragOver(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  return (
    <div className="database-page">
      <div className="header-container">
        <div className="ansicht-toggle">
          <button
            className={`ansicht-btn ${ansicht === "objekt" ? "active" : ""}`}
            onClick={() => switchAnsicht("objekt")}
          >
            Objektansicht
          </button>
          <button
            className={`ansicht-btn ${ansicht === "datei" ? "active" : ""}`}
            onClick={() => switchAnsicht("datei")}
          >
            Dateiansicht
          </button>
        </div>
        <h1 className="page-title">
          📊 Übersicht der Konten
        </h1>
        <button className="alle-konten-button" onClick={() => handleOpenPopup("")}>📋 Alle Konten</button>
      </div>

      {loading && <p>Lade Daten...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && konten.length > 0 && ansicht === "objekt" && (
        <>
          {/* Konten MIT Zuordnung */}
          <table className="overview-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Objekt</th>
                <th>IBAN</th>
                <th>Buchungen</th>
                <th>Neuestes Datum</th>
                <th>Ältestes Datum</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {mitZuordnung.map((konto, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td className="kontocode-cell">{konto.kontocode}</td>
                  <td className="iban-cell">{konto.iban}</td>
                  <td>{konto.anzahl_buchungen}</td>
                  <td>{konto.neuestes_datum}</td>
                  <td>{konto.aeltestes_datum}</td>
                  <td className="action-buttons">
                    <button className="query-button" onClick={() => handleOpenPopup(konto.iban)}>📋</button>
                    <button className="details-button" onClick={() => navigate(`/details/${konto.iban}`)}>🔍</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Konten OHNE Zuordnung — eingeklappt */}
          {ohneZuordnung.length > 0 && (
            <div className="ohne-zuordnung-section">
              <button
                className="ohne-zuordnung-toggle"
                onClick={() => setOhneZuordnungOpen(!ohneZuordnungOpen)}
              >
                {ohneZuordnungOpen ? "▼" : "▶"} Ohne Zuordnung ({ohneZuordnung.length})
              </button>
              {ohneZuordnungOpen && (
                <table className="overview-table ohne-zuordnung-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>IBAN</th>
                      <th>Buchungen</th>
                      <th>Neuestes Datum</th>
                      <th>Ältestes Datum</th>
                      <th>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ohneZuordnung.map((konto, index) => (
                      <tr key={index}>
                        <td>{mitZuordnung.length + index + 1}</td>
                        <td className="iban-cell">{konto.iban}</td>
                        <td>{konto.anzahl_buchungen}</td>
                        <td>{konto.neuestes_datum}</td>
                        <td>{konto.aeltestes_datum}</td>
                        <td className="action-buttons">
                          <button className="query-button" onClick={() => handleOpenPopup(konto.iban)}>📋</button>
                          <button className="details-button" onClick={() => navigate(`/details/${konto.iban}`)}>🔍</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {!loading && !error && konten.length > 0 && ansicht === "datei" && (
        <table className="overview-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Konto</th>
              <th>Buchungen</th>
              <th>Neuestes Datum</th>
              <th>Ältestes Datum</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {konten.map((konto, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{konto.auftragskonto}</td>
                <td>{konto.anzahl_buchungen}</td>
                <td>{konto.neuestes_datum}</td>
                <td>{konto.aeltestes_datum}</td>
                <td className="action-buttons">
                  <button className="query-button" onClick={() => handleOpenPopup(konto.auftragskonto)}>📋</button>
                  <button className="details-button" onClick={() => navigate(`/details/${konto.auftragskonto}`)}>🔍</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && konten.length === 0 && <p>Keine Konten gefunden.</p>}

      <button className="refresh-button" onClick={() => ansicht === "objekt" ? fetchObjektKonten() : fetchDateiKonten()}>
        🔄 Aktualisieren
      </button>

      {/* ── Query-Popup ── */}
      {selectedKonto !== null && (
        <div className="popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClosePopup(); }}>
          <div className="query-popup-box">
            <div className="query-popup-header">
              <h2>� Power Query – {selectedKonto ? `...${selectedKonto.slice(-8)}` : "Alle Konten"}</h2>
              <button className="close-btn" onClick={handleClosePopup}>×</button>
            </div>

            <div className="query-code-section">
              <pre className="query-code">{buildQueryCode()}</pre>

              {/* Toolbar: Deutsche Ausgabe + Spalten-Toggle */}
              <div className="query-toolbar">
                <button
                  className={`toolbar-btn ${deutscheAusgabe ? "active" : ""}`}
                  onClick={() => setDeutscheAusgabe(!deutscheAusgabe)}
                  title="Deutsche Formatierung (Datum DD.MM.YY, Beträge 1.000,00 €)"
                >
                  🇩🇪 Deutsche Ausgabe
                </button>
                <button
                  className={`toolbar-btn ${columnsOpen ? "active" : ""}`}
                  onClick={() => setColumnsOpen(!columnsOpen)}
                >
                  📋 Spalten ({queryColumns.length}/{ALL_QUERY_COLUMNS.length})
                </button>
              </div>

              {/* Spaltenauswahl (einklappbar) */}
              {columnsOpen && (
                <div className="column-selector">
                  <div className="column-selector-actions">
                    <button className="col-action-btn" onClick={() => setQueryColumns(ALL_QUERY_COLUMNS.map(c => c.key))}>Alle</button>
                    <button className="col-action-btn" onClick={() => setQueryColumns([])}>Keine</button>
                    <button className="col-action-btn" onClick={() => setQueryColumns(ALL_QUERY_COLUMNS.filter(c => c.default).map(c => c.key))}>Standard</button>
                  </div>
                  {ALL_QUERY_COLUMNS.map(col => (
                    <label key={col.key} className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={queryColumns.includes(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className="popup-buttons">
                <button className="copy-button" onClick={handleCopyToClipboard}>📋 Kopieren</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Migration Popup (unverändert) ── */}
      {showMigrationPopup && (
        <div className="popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeMigrationPopup(); }}>
          <div className="migration-popup">
            <div className="migration-popup-header">
              <h2>🔄 Datenmigration – Excel → Datenbank</h2>
              <button className="close-btn" onClick={closeMigrationPopup}>×</button>
            </div>

            {migrationError && (
              <div className="migration-error">
                ❌ {migrationError}
                <button onClick={() => setMigrationError(null)}>×</button>
              </div>
            )}

            <div className="migration-popup-body">
              {migrationStep === "upload" && (
                <div className="migration-upload-step">
                  <div
                    className={`drop-zone ${dragOver ? "drag-over" : ""} ${uploading ? "uploading" : ""}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <>
                        <div className="upload-spinner"></div>
                        <p>Excel wird analysiert...</p>
                      </>
                    ) : (
                      <>
                        <div className="drop-icon">📂</div>
                        <p><strong>Excel-Datei hier ablegen</strong></p>
                        <p className="drop-hint">oder klicken zum Auswählen (.xlsx, .xls)</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload(f);
                      e.target.value = "";
                    }}
                  />
                </div>
              )}

              {migrationStep === "mapping" && uploadInfo && (
                <div className="migration-mapping-step">
                  <div className="analyse-card">
                    <div className="analyse-header">
                      <span className="analyse-filename">📄 {uploadInfo.filename}</span>
                      {uploadInfo.sheets?.length > 1 && (
                        <select
                          className="sheet-select"
                          value={uploadInfo.active_sheet}
                          onChange={(e) => handleSheetChange(e.target.value)}
                        >
                          {uploadInfo.sheets.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="analyse-stats">
                      <span>📊 {uploadInfo.row_count} Zeilen</span>
                      <span>📋 {uploadInfo.column_count} Spalten</span>
                      <span>📑 Sheet: {uploadInfo.active_sheet}</span>
                    </div>

                    {uploadInfo.preview_rows?.length > 0 && (
                      <details className="preview-details">
                        <summary>Vorschau (erste 5 Zeilen)</summary>
                        <div className="preview-table-wrapper">
                          <table className="preview-table">
                            <thead>
                              <tr>
                                {uploadInfo.excel_headers?.map((h, i) => (
                                  <th key={i}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {uploadInfo.preview_rows.map((row, ri) => (
                                <tr key={ri}>
                                  {uploadInfo.excel_headers.map((h, ci) => (
                                    <td key={ci}>{row[h] ?? ""}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                  </div>

                  <div className="auftragskonto-row">
                    <label>Auftragskonto (IBAN):</label>
                    <input
                      type="text"
                      value={auftragskonto}
                      onChange={(e) => setAuftragskonto(e.target.value)}
                      placeholder="z.B. DE61207500000090683806 (oder per Mapping)"
                      className="auftragskonto-input"
                    />
                  </div>

                  <div className="mapping-section">
                    <h3>Spalten-Zuordnung</h3>
                    <div className="mapping-grid">
                      {dbColumns
                        .filter(col => !col.auto)
                        .map((col) => {
                          const isAutoMapped = autoMapping[col.name] && mapping[col.name] === autoMapping[col.name];
                          return (
                            <div key={col.name} className={`mapping-row ${isAutoMapped ? "auto-mapped" : ""}`}>
                              <div className="db-col-info">
                                <span className="db-col-name">{col.name}</span>
                                <span className="db-col-type">{col.type}</span>
                              </div>
                              <span className="mapping-arrow">→</span>
                              <select
                                value={mapping[col.name] || ""}
                                onChange={(e) => handleMappingChange(col.name, e.target.value)}
                                className={`mapping-select ${mapping[col.name] ? "has-value" : ""}`}
                              >
                                <option value="">-- nicht zuordnen --</option>
                                {uploadInfo.excel_headers?.map((h, i) => (
                                  <option key={i} value={h}>{h}</option>
                                ))}
                              </select>
                              {isAutoMapped && <span className="auto-badge">auto</span>}
                            </div>
                          );
                        })}

                      <div className="mapping-divider">Automatisch berechnete Spalten</div>
                      {dbColumns
                        .filter(col => col.auto)
                        .map((col) => (
                          <div key={col.name} className="mapping-row auto-col">
                            <div className="db-col-info">
                              <span className="db-col-name">{col.name}</span>
                              <span className="db-col-type">{col.type}</span>
                            </div>
                            <span className="mapping-arrow">→</span>
                            <span className="auto-label">automatisch</span>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="migration-actions">
                    <button className="btn-back" onClick={() => { setMigrationStep("upload"); setUploadInfo(null); setUploadId(null); }}>
                      ← Zurück
                    </button>
                    <button
                      className="btn-start-migration"
                      onClick={startMigration}
                      disabled={migrating || Object.values(mapping).filter(v => v).length === 0}
                    >
                      {migrating ? (
                        <><span className="upload-spinner small"></span> Importiere...</>
                      ) : (
                        `🚀 Migration starten (${uploadInfo.row_count} Zeilen)`
                      )}
                    </button>
                  </div>
                </div>
              )}

              {migrationStep === "done" && migrationResult && (
                <div className="migration-done-step">
                  <div className="done-icon">✅</div>
                  <h3>Migration abgeschlossen</h3>
                  <div className="done-stats">
                    <div className="done-stat">
                      <span className="done-stat-value">{migrationResult.rows_inserted}</span>
                      <span className="done-stat-label">Zeilen importiert</span>
                    </div>
                    <div className="done-stat">
                      <span className="done-stat-value">{migrationResult.total_rows}</span>
                      <span className="done-stat-label">Zeilen gesamt</span>
                    </div>
                    {migrationResult.errors?.length > 0 && (
                      <div className="done-stat error">
                        <span className="done-stat-value">{migrationResult.errors.length}</span>
                        <span className="done-stat-label">Fehler</span>
                      </div>
                    )}
                  </div>

                  {migrationResult.errors?.length > 0 && (
                    <details className="error-details">
                      <summary>Fehler anzeigen ({migrationResult.errors.length})</summary>
                      <ul className="error-list">
                        {migrationResult.errors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  <div className="migration-actions">
                    <button className="btn-start-migration" onClick={() => { closeMigrationPopup(); fetchObjektKonten(); }}>
                      Fertig
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <button className="migration-button" onClick={() => setShowMigrationPopup(true)}>
        🔄 Datenmigration
      </button>
    </div>
  );
};

export default DatabasePage;